import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";

import {
  getFontFamily,
  getSize,
  isTouchDevice,
} from "@racepointenergy/gatsby-theme-sitespark/src/components/Theme/utils";
import { useJsEnabled } from "@racepointenergy/gatsby-theme-sitespark/src/hooks";

import { BecomeADealer } from "./components/become-a-dealer";
import { Commercial } from "./components/commercial";
import { Residential } from "./components/residential";
import { Solar } from "./components/solar";
import { Utilities } from "./components/utilities";

import { energyPackagesListElement } from "./elements/energy-packages-list";

const EnergyPackages = styled.section`
  padding: 20px 0px 16px 0px;
  text-align: center;
  max-width: 100vw;
  width: 100%;
  @media screen and (min-width: ${getSize("mobile480")}) {
    padding: 32px 0px 28px 0px;
  }
  @media screen and (min-width: ${getSize("tablet720")}) {
    padding: 32px 50px 26px 50px;
  }
  @media screen and (min-width: ${getSize("tablet960")}) {
    padding: 24px 144px 16px 144px;
  }
  @media screen and (min-width: ${getSize("desktop1200")}) {
    padding: 29px 144px 24px 144px;
  }
  @media screen and (min-width: ${getSize("desktop1920")}) {
    padding: 76px 4.48%;
  }
`;

const StyledH2 = styled.h2`
  color: ${p => p.theme.colors.blueGrey};
  padding: 0px 40px;
  font-size: 36px;
  line-height: 44px;
  margin: 0px;
  margin-bottom: 34px;
  text-align: center;
  font-weight: 300;
  /* @media screen and (min-width: ${getSize("default")}) {} */
  @media screen and (min-width: ${getSize("mobile480")}) {
    margin-bottom: 36px;
    padding: 0px 90px;
  }
  @media screen and (min-width: ${getSize("tablet720")}) {
    margin-bottom: 40px;
  }
  @media screen and (min-width: ${getSize("tablet768")}) {
    margin-bottom: 48px;
  }
  @media screen and (min-width: ${getSize("tablet960")}) {
    margin-bottom: 33px;
  }
  @media screen and (min-width: ${getSize("desktop1200")}) {
    font-size: 48px;
    line-height: 58px;
    margin-bottom: 56px;
  }
  /* @media screen and (min-width: ${getSize("desktop1920")}) {} */
  ${getFontFamily("light")}
`;

const GrabToScrollWrap = styled.div`
  @media screen and (min-width: ${getSize("default")}) and (max-width: 719px) {
    overflow-x: auto;
    ${({ hideScrollbar }) => {
      if (hideScrollbar) {
        return `
        cursor: grab;
        scrollbar-width: none; /* Firefox */
        -ms-overflow-style: none; /* IE 10+ */
        ::-webkit-scrollbar {
          display: none; /* Chrome Safari */
        }`;
      }
      return "";
    }}
  }
`;

function debounceRaf(fn) {
  let queued;
  return function(...args) {
    if (queued) window.cancelAnimationFrame(queued);

    queued = window.requestAnimationFrame(fn.bind(fn, ...args));
  };
}

// only grab-to-scroll on screens less than 720px
const checkDisableGrabToScroll = () =>
  window.matchMedia(`(min-width: 720px)`).matches;

// returns boolean if UI should allow grab-to-scroll behavior
function useDisableGrabToScroll() {
  const [disableGrabToScroll, setDisableGrabToScroll] = useState(false);
  useEffect(() => {
    const onWindowResizeCheckGrabToScroll = debounceRaf(() => {
      setDisableGrabToScroll(checkDisableGrabToScroll());
    });
    window.addEventListener("resize", onWindowResizeCheckGrabToScroll);

    onWindowResizeCheckGrabToScroll(); // init

    // remove event listeners
    const cleanup = () => {
      window.removeEventListener("resize", onWindowResizeCheckGrabToScroll);
    };
    return cleanup;
  }, []);
  return disableGrabToScroll;
}

const noop = () => {};

function useGrabToScroll(grabToScrollElementRef) {
  const posRef = useRef({ top: 0, left: 0, x: 0, y: 0 });
  const disableGrabToScroll = useDisableGrabToScroll();

  useEffect(() => {
    // imperative grab-to-scroll behavior using window api
    const { current: grabToScrollElement } = grabToScrollElementRef;

    // touch and non-touch large-screen users don't need grab-to-scroll feature
    if (isTouchDevice() || disableGrabToScroll) {
      grabToScrollElement.style.cursor = "initial";
      // return noop so hook has consistent 'signature'
      return noop;
    }

    // add grab-to-scroll for non-touch JS users
    const onMouseUpStopGrabbing = () => {
      grabToScrollElement.style.cursor = "grab";
      grabToScrollElement.style.removeProperty("user-select");
      grabToScrollElement.removeEventListener(
        "mousemove",
        onMouseMoveMeasureDistance
      );
      grabToScrollElement.removeEventListener("mouseup", onMouseUpStopGrabbing);
      grabToScrollElement.removeEventListener(
        "mouseleave",
        onMouseUpStopGrabbing
      );
    };

    const onMouseMoveMeasureDistance = debounceRaf(mouseMoveEvent => {
      const { current: pos } = posRef;
      // Measure mouse distance
      const dx = mouseMoveEvent.clientX - pos.x;
      const dy = mouseMoveEvent.clientY - pos.y;

      // Update scroll position
      grabToScrollElement.scrollTop = pos.top - dy;
      grabToScrollElement.scrollLeft = pos.left - dx;
    });

    const onMouseDownStartGrabbing = mouseDownEvent => {
      grabToScrollElement.style.cursor = "grabbing";
      grabToScrollElement.style.userSelect = "none";

      posRef.current = {
        // Current scroll position
        left: grabToScrollElement.scrollLeft,
        top: grabToScrollElement.scrollTop,
        // Current mouse position
        x: mouseDownEvent.clientX,
        y: mouseDownEvent.clientY,
      };

      // Manually scroll element on mousemove
      grabToScrollElement.addEventListener(
        "mousemove",
        onMouseMoveMeasureDistance
      );
      // Stop listening for mousemove, reset grab scrolling
      grabToScrollElement.addEventListener("mouseleave", onMouseUpStopGrabbing);
      grabToScrollElement.addEventListener("mouseup", onMouseUpStopGrabbing);
    };
    // On mousedown, use grab to scroll element
    grabToScrollElement.addEventListener("mousedown", onMouseDownStartGrabbing);
    // reset scroll position when starting grab-to-scroll behavior, this is necessary when window size decreases
    grabToScrollElement.scrollTop = 0;
    grabToScrollElement.scrollLeft = 0;

    // remove all event listeners
    const cleanup = () => {
      grabToScrollElement.removeEventListener(
        "mousedown",
        onMouseDownStartGrabbing
      );
      grabToScrollElement.removeEventListener(
        "mouseleave",
        onMouseUpStopGrabbing
      );
      grabToScrollElement.removeEventListener(
        "mousemove",
        onMouseMoveMeasureDistance
      );
      grabToScrollElement.removeEventListener("mouseup", onMouseUpStopGrabbing);
    };
    return cleanup;
  }, [disableGrabToScroll, grabToScrollElementRef]);
}

export function Applications(props) {
  // So that we don't hide the scrollbar for users without JS, we disable it only after React (JS) mount
  // Unfortunately causes a waterfall loading effect as scrollbar CSS can't be animated/transitioned :(
  // The appropriate solution is not to hack on scroll behavior
  const hideScrollbar = useJsEnabled();
  const grabToScrollElementRef = useRef();
  useGrabToScroll(grabToScrollElementRef);
  return (
    <div>
      <Residential />
      <EnergyPackages>
        <StyledH2>
          Sample Energy
          <br
            css={`
              @media screen and (min-width: ${getSize("tablet720")}) {
                display: none;
              }
            `}
          />
          {" Packages"}
        </StyledH2>
        <GrabToScrollWrap
          hideScrollbar={hideScrollbar}
          ref={grabToScrollElementRef}
        >
          {energyPackagesListElement}
        </GrabToScrollWrap>
      </EnergyPackages>
      <Commercial />
      <Solar />
      <Utilities />
      <BecomeADealer />
    </div>
  );
}
